/**
 * 課題文編集ビュー（フォームUI）
 * レビュー完了・レビュー中トグル、フォームによる編集、保存
 */

import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Loader2, Save, ArrowLeft } from 'lucide-react';
import { InstructionFormEditor } from './InstructionFormEditor';
import type { CategoryTableData } from './ConditionCategoryTable';

interface InstructionTask {
  no: number;
  title: string;
  details?: string[];
  related_conditions?: string[];
}

interface InstructionFormData {
  summary?: string;
  tasks?: InstructionTask[];
  target_categories?: string[];
  target_category_levels?: Record<string, string[]>;
}

interface InstructionEditViewProps {
  instruction: {
    id: number;
    instruction_data: Record<string, unknown>;
    review_status: string;
  };
  onBack: () => void;
  onUpdate: (id: number, data: object) => Promise<void>;
  onUpdateReviewStatus: (id: number, status: 'approved' | 'draft' | 'in_review') => Promise<void>;
  /** 紐づく条件の categoryTable（Lvホバーで内容を表示するのに使う） */
  categoryTable?: CategoryTableData | null;
  /** 紐づく条件の condition_data。V3.1 の可変カテゴリ・可変Lvを編集対象にする。 */
  conditionData?: Record<string, unknown> | null;
}

export function InstructionEditView({
  instruction,
  onBack,
  onUpdate,
  onUpdateReviewStatus,
  categoryTable,
  conditionData,
}: InstructionEditViewProps) {
  const [formData, setFormData] = useState<InstructionFormData>(() => ({
    summary: (instruction.instruction_data?.summary as string) ?? '',
    tasks: (instruction.instruction_data?.tasks as InstructionTask[]) ?? [],
    target_categories: (instruction.instruction_data?.target_categories as string[]) ?? [],
    target_category_levels: (instruction.instruction_data?.target_category_levels as Record<string, string[]>) ?? {},
  }));

  useEffect(() => {
    setFormData({
      summary: (instruction.instruction_data?.summary as string) ?? '',
      tasks: (instruction.instruction_data?.tasks as InstructionTask[]) ?? [],
      target_categories: (instruction.instruction_data?.target_categories as string[]) ?? [],
      target_category_levels: (instruction.instruction_data?.target_category_levels as Record<string, string[]>) ?? {},
    });
  }, [instruction.id, instruction.instruction_data]);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  const isApproved = instruction.review_status === 'approved';

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);
      // 既存のinstruction_data（scenario_phase等の追加フィールド）とマージ。
      // 廃止フィールド（difficulty/patient/context）は保存時に除去してDBから消していく。
      const { difficulty: _omitDiff, patient: _omitPatient, context: _omitCtx, ...rest } = instruction.instruction_data as Record<string, unknown>;
      // 保存時に task.no を表示順に再採番する（既存DBで no=0 の不整合を修復）
      const normalizedTasks = (formData.tasks ?? []).map((t, i) => ({ ...t, no: i + 1 }));
      const merged = { ...rest, ...formData, tasks: normalizedTasks };
      await onUpdate(instruction.id, merged);
    } catch (e) {
      console.error('課題文保存エラー:', e);
      alert('保存に失敗しました。');
    } finally {
      setSaving(false);
    }
  }, [instruction.id, instruction.instruction_data, formData, onUpdate]);

  const handleToggleReview = useCallback(async (checked: boolean) => {
    try {
      setToggling(true);
      await onUpdateReviewStatus(instruction.id, checked ? 'approved' : 'in_review');
    } finally {
      setToggling(false);
    }
  }, [instruction.id, onUpdateReviewStatus]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              一覧へ
            </Button>
            <CardTitle className="text-lg">課題文 #{instruction.id} 編集</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">レビュー中</span>
            <Switch
              checked={isApproved}
              onCheckedChange={handleToggleReview}
              disabled={toggling}
            />
            <span className="text-sm text-muted-foreground">レビュー完了</span>
            {toggling && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-h-[calc(100vh-220px)] overflow-y-auto pr-2">
          <InstructionFormEditor
            data={formData}
            onChange={setFormData}
            disabled={isApproved}
            categoryTable={categoryTable}
            conditionData={conditionData}
          />
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isApproved || saving}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
